import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import PriceBreakdown from "../components/PriceBreakdown";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import { useCart } from "../context/CartContext";
import "./PaymentPage.css";

function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchCart } = useCart();

  const bookingId = searchParams.get("booking");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  // Load booking details (single or batch)
  useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axiosClient
      .get(`/bookings/${bookingId}`)
      .then((res) => {
        if (res.data?.multiple) {
          setBookings(res.data.bookings || []);
        } else if (Array.isArray(res.data)) {
          setBookings(res.data);
        } else if (res.data) {
          setBookings([res.data]);
        }
      })
      .catch((err) => {
        console.log("Error loading booking:", err);
        toast.error("Failed to load booking details");
      })
      .finally(() => setLoading(false));
  }, [bookingId]);

  const totalAmount = bookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const startPayment = async () => {
    const userToken = localStorage.getItem("token");
    if (!userToken) {
      toast.error("Please login to continue");
      return;
    }

    if (!totalAmount || totalAmount <= 0) {
      toast.error("Invalid payment amount");
      return;
    }

    const razorLoaded = await loadRazorpayScript();
    if (!razorLoaded) {
      toast.error("Failed to load Razorpay SDK");
      return;
    }

    try {
      // Create order
      const { data } = await axiosClient.post(
        "/payments/create-order",
        {
          amount: totalAmount, // RUPEES only
          bookingId: bookingId,
        },
        {
          headers: { Authorization: `Bearer ${userToken}` },
        }
      );

      const order = data.order;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: totalAmount,
        currency: "INR",
        name: "Bike Rental Booking",
        description: `Payment for ${bookings.length} ${bookings.length === 1 ? "bike" : "bikes"}`,
        order_id: order.id,

        handler: async function (response) {
          setIsVerifying(true);
          try {
            const verifyResponse = await axiosClient.post(
              "/payments/verify",
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                bookingId,
              },
              {
                headers: { Authorization: `Bearer ${userToken}` },
              }
            );

            if (verifyResponse.data.success) {
              if (fetchCart) fetchCart();
              const firstId = bookings[0]?._id || bookingId;
              navigate(
                `/payment-success?payment_id=${response.razorpay_payment_id}&booking_id=${firstId}`
              );
            } else {
              toast.error("Payment verification failed");
              setIsVerifying(false);
            }
          } catch (error) {
            console.error("Verification error:", error);
            toast.error("Payment verification failed");
            setIsVerifying(false);
          }
        },

        theme: { color: "#2dd4bf" },
      };

      const razor = new window.Razorpay(options);
      razor.open();
    } catch (err) {
      toast.error(err.response?.data?.message || "Order creation failed");
    }
  };

  if (loading || bookings.length === 0) {
    return (
      <div className="payment-page">
        <LoadingSpinner message="Loading payment details" sub="Fetching your booking info…" />
      </div>
    );
  }

  const isSingle = bookings.length === 1;
  const singleBooking = bookings[0];

  return (
    <div className="payment-page">
      {/* LOADING OVERLAY */}
      {isVerifying && (
        <LoadingSpinner overlay message="Processing Payment" sub="Please don’t close this window or press back…" />
      )}

      <div className="payment-header">
        <button className="page-back-btn" onClick={() => navigate(-1)} title="Go back">
          <span className="back-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </span>
          Back
        </button>
        <h1>Payment Summary</h1>
      </div>

      <div className="payment-card">
        <div className="payment-left-panel">
          <h2 style={{ marginBottom: "20px", color: "var(--text)" }}>
            {isSingle ? "Booking Details" : `Bookings (${bookings.length} Bikes)`}
          </h2>

          <div
            className="payment-bike-details"
            style={{ border: "none", padding: "0", background: "transparent", margin: "0" }}
          >
            {isSingle ? (
              <>
                <div className="payment-detail-row">
                  <span className="payment-label">Bike</span>
                  <span className="payment-value">{singleBooking.bikeId?.name}</span>
                </div>
                <div className="payment-detail-row">
                  <span className="payment-label">Pickup</span>
                  <span className="payment-value">{new Date(singleBooking.startDate).toLocaleString()}</span>
                </div>
                <div className="payment-detail-row">
                  <span className="payment-label">Drop-off</span>
                  <span className="payment-value">{new Date(singleBooking.endDate).toLocaleString()}</span>
                </div>
              </>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "16px" }}>
                {bookings.map((b, idx) => (
                  <div
                    key={b._id || idx}
                    style={{
                      background: "#ffffff",
                      border: "1px solid var(--border)",
                      borderRadius: "12px",
                      padding: "14px",
                    }}
                  >
                    <div className="payment-detail-row" style={{ marginBottom: "8px" }}>
                      <span className="payment-label" style={{ fontWeight: 700 }}>
                        Bike #{idx + 1}
                      </span>
                      <span className="payment-value" style={{ fontWeight: 700, color: "var(--text)" }}>
                        {b.bikeId?.name}
                      </span>
                    </div>
                    <div className="payment-detail-row" style={{ fontSize: "13px" }}>
                      <span className="payment-label">Dates</span>
                      <span className="payment-value" style={{ fontSize: "13px" }}>
                        {new Date(b.startDate).toLocaleDateString()} → {new Date(b.endDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="payment-detail-row" style={{ marginTop: "6px", paddingTop: "6px", borderTop: "1px dashed var(--border)" }}>
                      <span className="payment-label">Price</span>
                      <span className="payment-value" style={{ color: "var(--mint-dark)", fontWeight: 700 }}>
                        ₹{b.totalAmount}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="payment-total-row">
              <span className="payment-total-label">
                {isSingle ? "Total Amount" : `Grand Total (${bookings.length} Bikes)`}
              </span>
              <span className="payment-total-price">₹{totalAmount}</span>
            </div>

            <button className="razorpay-btn" onClick={startPayment}>
              Pay Securely Now (₹{totalAmount})
            </button>
          </div>
        </div>

        <div className="payment-right-panel">
          <h2 style={{ marginBottom: "16px", color: "var(--text)" }}>Price Breakdown</h2>

          {isSingle ? (
            <PriceBreakdown
              pricePerDay={singleBooking.bikeId?.pricePerDay}
              startDate={singleBooking.startDate}
              endDate={singleBooking.endDate}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxHeight: "420px", overflowY: "auto", paddingRight: "4px" }}>
              {bookings.map((b, idx) => (
                <div key={b._id || idx} style={{ borderBottom: idx < bookings.length - 1 ? "1px solid var(--border)" : "none", paddingBottom: "14px" }}>
                  <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--mint-dark)", marginBottom: "8px" }}>
                    {b.bikeId?.name}
                  </h3>
                  <PriceBreakdown
                    pricePerDay={b.bikeId?.pricePerDay}
                    startDate={b.startDate}
                    endDate={b.endDate}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default PaymentPage;
